import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Warning, ChatText, ArrowRight, EnvelopeOpen } from "@phosphor-icons/react";

/**
 * Unified Office Inbox — one live feed of every open access request +
 * unread field message across ALL jobs in the tenant.
 * Route: /admin/inbox
 */
export default function AdminInbox() {
  const [data, setData] = useState({ items: [], open_access_count: 0, unread_message_count: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | access | message

  const load = async () => {
    try {
      const { data } = await api.get("/inbox");
      setData(data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const markAllRead = async () => {
    const msgIds = (data.items || []).filter(i => i.kind === "message").map(i => i.id);
    if (msgIds.length === 0) { toast.info("Nothing to mark"); return; }
    try {
      await api.post("/inbox/mark-read", { message_ids: msgIds });
      toast.success(`Marked ${msgIds.length} message${msgIds.length === 1 ? "" : "s"} as read`);
      await load();
    } catch (e) { toast.error("Could not mark read"); }
  };

  const items = (data.items || []).filter(i => {
    if (filter === "access") return i.kind === "access_request";
    if (filter === "message") return i.kind === "message";
    return true;
  });

  const minsAgo = (iso) => {
    if (!iso) return "—";
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s/60)}m`;
    if (s < 86400) return `${Math.floor(s/3600)}h`;
    return `${Math.floor(s/86400)}d`;
  };

  return (
    <AdminShell>
      <div className="p-4 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Office Inbox</h1>
            <p className="mt-1 text-sm text-slate-500 font-medium">
              Live feed — access requests + unread field messages across every job.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={markAllRead} data-testid="inbox-mark-all-read"
              className="tactile-btn bg-white border-2 border-[#0f172a] h-11 px-4 font-black uppercase text-xs tracking-wider inline-flex items-center gap-2">
              <EnvelopeOpen weight="bold" size={16}/> Mark all read
            </button>
          </div>
        </div>

        {/* Stat pills */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <button onClick={() => setFilter("access")}
            data-testid="inbox-stat-access"
            className={`rugged-card p-4 text-left transition-all ${
              filter === "access" ? "ring-4 ring-red-500 ring-offset-2" : ""
            }`}>
            <div className="flex items-center gap-2">
              <Warning weight="fill" size={18} className="text-red-600"/>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Access Needed</div>
            </div>
            <div className="mt-1 font-display font-black text-3xl text-red-700">{data.open_access_count}</div>
          </button>
          <button onClick={() => setFilter("message")}
            data-testid="inbox-stat-messages"
            className={`rugged-card p-4 text-left transition-all ${
              filter === "message" ? "ring-4 ring-[#00C4CC] ring-offset-2" : ""
            }`}>
            <div className="flex items-center gap-2">
              <ChatText weight="fill" size={18} className="text-[#00C4CC]"/>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Unread Messages (24h)</div>
            </div>
            <div className="mt-1 font-display font-black text-3xl text-[#0e7490]">{data.unread_message_count}</div>
          </button>
          <button onClick={() => setFilter("all")}
            data-testid="inbox-stat-all"
            className={`rugged-card p-4 text-left transition-all ${
              filter === "all" ? "ring-4 ring-slate-800 ring-offset-2" : ""
            }`}>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Total in Feed</div>
            <div className="mt-1 font-display font-black text-3xl">{(data.items || []).length}</div>
          </button>
        </div>

        {/* Feed */}
        <div className="mt-6 space-y-3">
          {loading ? (
            <div className="rugged-card p-8 text-center text-slate-400 font-bold">Loading…</div>
          ) : items.length === 0 ? (
            <div className="rugged-card p-8 text-center" data-testid="inbox-empty">
              <EnvelopeOpen weight="bold" size={40} className="mx-auto text-slate-300"/>
              <div className="mt-2 font-display font-black text-xl">All clear</div>
              <div className="text-sm text-slate-500 font-medium">
                {filter === "access" ? "No open access requests." :
                 filter === "message" ? "No unread field messages." :
                 "Nothing needs your attention right now."}
              </div>
            </div>
          ) : items.map(it => (
            <Link key={`${it.kind}-${it.id}`} to={`/admin/jobs/${it.job_id}`}
              data-testid={`inbox-item-${it.id}`}
              className={`block rugged-card p-4 hover:shadow-lg transition-all border-l-4 ${
                it.kind === "access_request" ? "border-red-500 bg-red-50" : "border-[#00C4CC] bg-cyan-50/40"
              }`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  {it.kind === "access_request" ? (
                    <Warning weight="fill" size={22} className="text-red-600 mt-1 flex-shrink-0"/>
                  ) : (
                    <ChatText weight="fill" size={22} className="text-[#00C4CC] mt-1 flex-shrink-0"/>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${
                        it.kind === "access_request" ? "bg-red-600 text-white" : "bg-[#00C4CC] text-white"
                      }`}>
                        {it.kind === "access_request" ? `🔐 ${it.reason_label}` : "💬 Message"}
                      </span>
                      {it.kind === "access_request" && it.stage !== "staff" && (
                        <span className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-orange-600 text-white">
                          Escalated · {it.stage}
                        </span>
                      )}
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        {minsAgo(it.created_at)} ago
                      </span>
                    </div>
                    <div className="mt-1 font-black text-slate-900">
                      {it.worker_name}
                      <span className="text-slate-400 font-medium"> · {it.property_address}</span>
                      {it.unit && <span className="ml-1 px-1.5 py-0.5 bg-fuchsia-600 text-white font-black uppercase text-[10px] tracking-widest rounded-sm">Apt {it.unit}</span>}
                    </div>
                    <div className="mt-1 text-sm text-slate-700 truncate">
                      {it.kind === "access_request"
                        ? (it.notes || `Reason: ${it.reason_label}`)
                        : it.text}
                    </div>
                  </div>
                </div>
                <ArrowRight weight="bold" size={20} className="text-slate-400 flex-shrink-0 mt-1"/>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </AdminShell>
  );
}
